import { useContext } from 'react'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { format } from 'date-fns'
import { useRouter } from 'next/router'

// Libs
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

// Components
import Image from '@/components/common/Image'
import Button from '@/components/common/Button'
import Link from '@/components/common/Link'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { VALID_BRAND_NAME } from '@/constants/brand'
import { EXTERNAL_LINK } from '@/constants/externalLink'

// Types
import { IRoomCover } from '@/types/room'

// Contexts
import { LiveRoomContext } from '@/components/pages/LiveRoom/index'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

type RoomCoverProps = IRoomCover & {
  className?: string
}

const HIDE_MORE_BUTTON_BRAND = new Set([
  VALID_BRAND_NAME.UDESIGN,
  VALID_BRAND_NAME.EGL_TOURS,
  VALID_BRAND_NAME.TEN_MART,
  VALID_BRAND_NAME.SOTHEBYS,
  VALID_BRAND_NAME.CORNEY_BARROW,
  VALID_BRAND_NAME.WINNER_PICKLE_BALL,
  VALID_BRAND_NAME.PACIFIC_PINE_SPORTS,
  VALID_BRAND_NAME.KB_JEWELS,
  VALID_BRAND_NAME.KIYAAN_JEWELLERY,
  VALID_BRAND_NAME.PXEC,
  VALID_BRAND_NAME.SQUARE_RECORDS,
  VALID_BRAND_NAME.HKFEC,
  VALID_BRAND_NAME.XORRO,
  VALID_BRAND_NAME.AREA02,
  VALID_BRAND_NAME.GLOBAL_MALL,
])

const RoomCover = (props: RoomCoverProps) => {
  const { className, coverPhotoUrl, isPast, title, hostMemberInfoId, kolName, startTime } = props

  const { t } = useTranslation()

  const liveRoomContext: any = useContext(LiveRoomContext)

  const { isValidBrandName, brandName } = liveRoomContext

  const router = useRouter()

  const formatStartTime = startTime ? format(new Date(startTime), 'yyyy/MM/dd HH:mm') : ''

  const handleCoverClick = async () => {
    if (!isValidBrandName) {
      handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${hostMemberInfoId}` })
    } else {
      let url = EXTERNAL_LINK.PCHOME_OFFICIAL_SITE

      if (isValidBrandName) {
        switch (brandName) {
          case VALID_BRAND_NAME.STRAWBERRY:
            url = EXTERNAL_LINK.STRAWBERRY_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.PCHOME:
            url = EXTERNAL_LINK.PCHOME_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.ECOSMETICS:
            url = EXTERNAL_LINK.ECOSMETICS_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.UDESIGN:
            url = EXTERNAL_LINK.UDESIGN_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.EGL_TOURS:
            url = EXTERNAL_LINK.EGL_TOURS_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.TEN_MART:
            url = EXTERNAL_LINK.TEN_MART_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.SOTHEBYS:
            url = EXTERNAL_LINK.SOTHEBYS_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.CORNEY_BARROW:
            url = EXTERNAL_LINK.CORNEY_BARROW_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.WINNER_PICKLE_BALL:
            url = EXTERNAL_LINK.WINNER_PICKLE_BALL_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.PACIFIC_PINE_SPORTS:
            url = EXTERNAL_LINK.PACIFIC_PINE_SPORTS_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.KB_JEWELS:
            url = EXTERNAL_LINK.KB_JEWELS_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.KIYAAN_JEWELLERY:
            url = EXTERNAL_LINK.KIYAAN_JEWELLERY_INSTAGRAM
            break
          case VALID_BRAND_NAME.PXEC:
            url = EXTERNAL_LINK.PXEC_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.SQUARE_RECORDS:
            url = EXTERNAL_LINK.SQUARE_RECORDS_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.HKFEC:
            url = EXTERNAL_LINK.HKFEC_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.XORRO:
            url = EXTERNAL_LINK.XORRO_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.AREA02:
            url = EXTERNAL_LINK.AREA02_OFFICIAL_SITE
            break
          case VALID_BRAND_NAME.GLOBAL_MALL:
            url = EXTERNAL_LINK.GLOBAL_MALL_OFFICIAL_SITE
            break
          default:
            break
        }
      }

      window.open(url)
    }
  }

  const getMoreLiveRoomPath = () => {
    const SHOP_LIVE_HOMEPAGE = ROUTE_PATH.LIVE
    let result = SHOP_LIVE_HOMEPAGE

    if (isValidBrandName) {
      switch (brandName) {
        case VALID_BRAND_NAME.STRAWBERRY:
          result = EXTERNAL_LINK.STRAWBERRY_OFFICIAL_SITE
          break
        case VALID_BRAND_NAME.PCHOME:
          result = EXTERNAL_LINK.PCHOME_OFFICIAL_SITE
          break
        case VALID_BRAND_NAME.ECOSMETICS:
          result = EXTERNAL_LINK.ECOSMETICS_OFFICIAL_SITE
          break
        default:
          break
      }
    }

    return result
  }

  return (
    <div className={cx('roomCover', className)}>
      <div className={cx('roomCover-background')}>
        <Image src={coverPhotoUrl} fill alt='kol cover' style={{ objectFit: 'cover' }} sizes='300px' />
      </div>

      <div className={cx('roomCover-cover')} />

      <div className={cx('roomCover-content')}>
        <div className={cx('roomCover-content-cover')} onClick={handleCoverClick}>
          <div className={cx('roomCover-content-cover-image')}>
            <Image src={coverPhotoUrl} width={164} height={206} alt='kol cover' style={{ objectFit: 'cover' }} />
          </div>
          <div className={cx('roomCover-content-cover-text')}>
            <p className={cx('roomCover-content-cover-text-name')}>{kolName}</p>
            <p className={cx('roomCover-content-cover-text-title')}>{title}</p>
          </div>
        </div>

        <p className={cx('roomCover-content-title')}>{isPast ? t('LiveRoom_Cover_Past') : t('LiveRoom_Cover_Future')}</p>
        {!isPast && <p className={cx('roomCover-content-time')}>{t('LiveRoom_Cover_Future_Time', { startTime: formatStartTime })}</p>}
        {isValidBrandName ? (
          <p className={cx('roomCover-content-description')}>
            {isPast && !isValidBrandName && t('LiveRoom_Cover_Past_Description')}
            {!isPast && t('LiveRoom_Cover_Future_Description')}
          </p>
        ) : (
          <p className={cx('roomCover-content-description')}>
            {isPast ? t('LiveRoom_Cover_Past_Description') : t('LiveRoom_Cover_Future_Description')}
          </p>
        )}

        {!HIDE_MORE_BUTTON_BRAND.has(brandName) && (
          <Link pathname={getMoreLiveRoomPath()}>
            <Button className={cx('roomCover__action-more')} size='sm'>
              {t('Common_More_Live_Streaming_Rooms')}
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

export default RoomCover
